import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { networkService } from "../../../config/axios";
import CustomTagsInput from "../../../CommonComponents/Tags/CustomTagsInput";

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SORT_OPTIONS = [
  { value: "created_desc", label: "Newest first" },
  { value: "created_asc", label: "Oldest first" },
  { value: "account_asc", label: "Account name A–Z" },
  { value: "account_desc", label: "Account name Z–A" },
];
const DEFAULT_SORT = "created_desc";

const KdfFilerForm = ({ triggerApplyFilterEvent, kdfTitleDefault = '', isOpen = true, quickBarTarget = null }) => {
  const [kdfTitle, setKdfTitle] = useState(kdfTitleDefault);
  const [kdfTags, setKdfTags] = useState({ name: "", tags: [] });
  const [status, setStatus] = useState("All");
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [loading, setLoading] = useState(false);
  const [titleSearchList, setTitleSearchList] = useState([]);
  const [tagsSearchList, setTagsSearchList] = useState([]);
  const [noMatchFound, setNoMatchFound] = useState(false); // For displaying "No match found"

  const [ownerName, setOwnerName] = useState("");
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [ownerSearchList, setOwnerSearchList] = useState([]);
  const [isOwnerSelected, setIsOwnerSelected] = useState(false);

  const [debouncedKdfTags, setDebouncedKdfTags] = useState(kdfTags?.name);

  const [isTitleSelected, setIsTitleSelected] = useState(false);
  const [isTagSelected, setIsTagSelected] = useState(false);
  // tag signature dedupe ke liye
const tagSig = (tagsArr) => (Array.isArray(tagsArr) ? tagsArr.map(t => t.id).sort().join(",") : "");

// last applied ko yaad rakhne ke liye
const lastAppliedTagSigRef = useRef(tagSig([]));


  const debouncedKdfTitle = useDebounce(kdfTitle, 1000);
  const debouncedOwnerName = useDebounce(ownerName, 1000);

  // Search for title
  const titleSearch = async () => {
    if (!debouncedKdfTitle || isTitleSelected) return;
    let { data, status } = await networkService(
      `kdfs.json?title=${debouncedKdfTitle}`,
      "GET"
    );
    if (status === 200) {
      setTitleSearchList(data);
    } else {
      setTitleSearchList([]);
    }
  };

  useEffect(() => {
    if (debouncedKdfTitle && !isTitleSelected) {
      titleSearch();
    } else {
      setTitleSearchList([]);
    }
  }, [debouncedKdfTitle, isTitleSelected]);

  useEffect(() => {
  const currentSig = tagSig(kdfTags.tags);
  if (currentSig !== lastAppliedTagSigRef.current) {
    // naya tag add/remove hua — abhi ke tags se apply karo
    applyFilters(undefined, { finalTags: kdfTags.tags });
    lastAppliedTagSigRef.current = currentSig;
  }
}, [kdfTags.tags]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault(); // Default form submit रोकने के लिए
      applyFilters(event); // Apply filters on Enter key press
    }
  };

  const handleKdfTitleChange = (e) => {
    setKdfTitle(e.target.value);
    setIsTitleSelected(false); // Reset flag when typing
  };

  const handleTitleSelect = (item) => {
  setKdfTitle(item.title);
  setTitleSearchList([]);
  setIsTitleSelected(true);
  // Immediately apply with the selected title
  applyFilters(undefined, { title: item.title });
};

  // Search for tags
  const tagsSearch = async (tagName) => {
    if (tagName) {
      let { data, status } = await networkService(
        `tags.json?tag_name=${tagName}&items=doc_list`,
        "GET"
      );
      if (status === 200) {
        setTagsSearchList(data);
      } else {
        setTagsSearchList([]);
      }
    } else {
      setTagsSearchList([]);
    }
  };

  useEffect(() => {
    if (debouncedKdfTags) {
      tagsSearch(debouncedKdfTags); // Trigger tags search only when debounced tag name is updated
    }
  }, [debouncedKdfTags]);

  const handleKdfTagsInputChange = (e) => {
    setKdfTags((prev) => ({ ...prev, name: e }));
    setDebouncedKdfTags(e);
    setNoMatchFound(false); // Reset no match found when user types
  };

  const handleTagSelect = (item) => {
  const alreadyExists = kdfTags.tags.some(tag => tag.id === item.id);
  const newTags = alreadyExists ? kdfTags.tags : [...kdfTags.tags, item];

  setKdfTags((prev) => ({
    ...prev,
    tags: newTags,
  }));
  setTagsSearchList([]);
  setNoMatchFound(false);
};

  const handleStatusChange = (e) => {
  const newStatus = e.target.textContent;
  setStatus(newStatus);
  // Immediately apply filters using the new status (override)
  applyFilters(undefined, { status: newStatus });
};

  const handleSortChange = (value) => {
    setSort(value);
    applyFilters(undefined, { sort: value });
  };

  // Search for owners
  const ownerSearch = async () => {
    if (!debouncedOwnerName || isOwnerSelected) return;
    let { data, status } = await networkService(
      `docs/owners.json?name=${encodeURIComponent(debouncedOwnerName)}`,
      "GET"
    );
    if (status === 200) {
      setOwnerSearchList(data);
    } else {
      setOwnerSearchList([]);
    }
  };

  useEffect(() => {
    if (debouncedOwnerName && !isOwnerSelected) {
      ownerSearch();
    } else {
      setOwnerSearchList([]);
    }
  }, [debouncedOwnerName, isOwnerSelected]);

  const handleOwnerNameChange = (e) => {
    setOwnerName(e.target.value);
    setIsOwnerSelected(false);
    if (e.target.value === "" && selectedOwner) {
      setSelectedOwner(null);
      applyFilters(undefined, { ownerId: null });
    }
  };

  const handleOwnerSelect = (item) => {
    setOwnerName(item.name);
    setSelectedOwner(item);
    setOwnerSearchList([]);
    setIsOwnerSelected(true);
    // Immediately apply with the selected owner
    applyFilters(undefined, { ownerId: item.id });
  };

  const applyFilters = async (e, overrides = {}) => {
  if (e?.preventDefault) e.preventDefault();
  setLoading(true);

  // ---- pick effective values (overrides win over state) ----
  const effectiveStatus = overrides.status ?? status;
  const effectiveTitle  = overrides.title  ?? kdfTitle;
  const effectiveSort   = overrides.sort   ?? sort;

  // Owner: use the explicitly selected owner's id. Otherwise, if the user just typed
  // text and hit Apply/Enter without picking a suggestion, resolve it ourselves —
  // the backend only filters by owner_id, so we look the typed name up directly
  // (not the debounced ownerSearchList, which may not have resolved yet) and filter
  // by every matching owner's id.
  let effectiveOwnerId = overrides.hasOwnProperty("ownerId") ? overrides.ownerId : selectedOwner?.id;
  if (!effectiveOwnerId && !overrides.hasOwnProperty("ownerId") && ownerName) {
    const { data, status: ownerStatus } = await networkService(
      `docs/owners.json?name=${encodeURIComponent(ownerName)}`,
      "GET"
    );
    if (ownerStatus === 200 && Array.isArray(data) && data.length > 0) {
      effectiveOwnerId = data.length === 1 ? data[0].id : data.map((owner) => owner.id);
    }
  }

  setTitleSearchList([]);
  setTagsSearchList([]);
  setNoMatchFound(false);
  setOwnerSearchList([]);

  // Start from tags passed in overrides (e.g., when user clicked a tag),
  // otherwise use state.
  let finalTags = Array.isArray(overrides.finalTags)
    ? [...overrides.finalTags]
    : [...kdfTags.tags];

  // If typing path is used (debouncedKdfTags present) and NO explicit overrides,
  // keep your existing "matchingTag" auto-push logic.
  if (debouncedKdfTags && !overrides.finalTags) {
    const matchingTag = tagsSearchList.find(
      tag => tag.name.toLowerCase() === debouncedKdfTags.toLowerCase()
    );
    const alreadyExists = finalTags.some(
      tag => tag.name.toLowerCase() === debouncedKdfTags.toLowerCase()
    );
    if (matchingTag && !alreadyExists) {
      finalTags.push(matchingTag);
      setKdfTags((prev) => ({
        ...prev,
        tags: finalTags,
        name: "",
      }));
      setNoMatchFound(false);
    }
  }

  // ---- build payload as before, but with effective values ----
  let payload = {};
  if (effectiveTitle !== "") payload["name"] = effectiveTitle;

  if (effectiveStatus?.toLowerCase() === "published") payload["published"] = true;
  else if (effectiveStatus?.toLowerCase() === "unpublished") payload["published"] = false;
  else if (effectiveStatus?.toLowerCase() === "private") payload["private_flag"] = true;
  else if (effectiveStatus?.toLowerCase() === "public") payload["private_flag"] = false;
  else if (effectiveStatus?.toLowerCase() === "my kdfs") payload["my_kdfs"] = true;

  if (!noMatchFound && finalTags.length > 0) {
    payload["tag_ids"] = finalTags.map((tag) => tag.id);
  }

  if (effectiveOwnerId) payload["owner_id"] = effectiveOwnerId;
  if (effectiveSort !== DEFAULT_SORT) payload["sort"] = effectiveSort;

  await triggerApplyFilterEvent(payload);
  setLoading(false);
};

  const resetFilters = () => {
    setKdfTitle("");
    setKdfTags({ name: "", tags: [] });
    setStatus("All");
    setSort(DEFAULT_SORT);
    setOwnerName("");
    setSelectedOwner(null);
    setIsOwnerSelected(false);
    triggerApplyFilterEvent();
  };

  const renderSearchInput = () => (
    <div className="position-relative kdf-filter-search">
      <input
        className="kdf-Tags2 ms-0"
        type="text"
        placeholder="Search here for any KDF"
        value={kdfTitle}
        onChange={handleKdfTitleChange}
        onKeyDown={handleKeyDown}
      />
      {titleSearchList.length > 0 && (
        <ul className="kdf-title-drop-ul">
          {titleSearchList.map((item, index) => (
            <li
              key={index}
              className="cursor-pointer kdf-title-drop-li"
              onClick={() => handleTitleSelect(item)}
            >
              {item.title}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderStatusDropdown = () => (
    <div className="all-btn">
      <div className="dropdown">
        <a
          className="btn btn-secondary dropdown-toggle"
          href="#"
          role="button"
          id="dropdownMenuLink"
          data-bs-toggle="dropdown"
          aria-expanded="false"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            fill="none"
            viewBox="0 0 24 24"
          >
            <mask
              id="mask0_8_1828"
              width="24"
              height="24"
              x="0"
              y="0"
              maskUnits="userSpaceOnUse"
              style={{ maskType: "alpha" }}
            >
              <path fill="#D9D9D9" d="M0 0h24v24H0z"></path>
            </mask>
            <g mask="url(#mask0_8_1828)">
              <path fill="#294561" d="M10 18v-2h4v2zm-4-5v-2h12v2zM3 8V6h18v2z"></path>
            </g>
          </svg>
          &nbsp;
          {status}
        </a>
        <ul className="dropdown-menu" aria-labelledby="dropdownMenuLink">
          <li className="dropdown-item" onClick={handleStatusChange}>
            All
          </li>
          <li className="dropdown-item" onClick={handleStatusChange}>
            Published
          </li>
          <li className="dropdown-item" onClick={handleStatusChange}>
            Unpublished
          </li>
          <li className="dropdown-item" onClick={handleStatusChange}>
            Private
          </li>
          <li className="dropdown-item" onClick={handleStatusChange}>
            Public
          </li>
          <li className="dropdown-item" onClick={handleStatusChange}>
            My KDFs
          </li>
        </ul>
      </div>
    </div>
  );

  const renderSortDropdown = () => (
    <div className="all-btn kdf-filter-sort">
      <div className="dropdown">
        <a
          className="btn btn-secondary dropdown-toggle"
          href="#"
          role="button"
          id="kdfSortMenuLink"
          data-bs-toggle="dropdown"
          aria-expanded="false"
          aria-label="Sort KDFs"
        >
          Sort: {SORT_OPTIONS.find((o) => o.value === sort)?.label}
        </a>
        <ul className="dropdown-menu" aria-labelledby="kdfSortMenuLink">
          {SORT_OPTIONS.map((option) => (
            <li
              key={option.value}
              className={`dropdown-item${option.value === sort ? " active" : ""}`}
              onClick={() => handleSortChange(option.value)}
            >
              {option.label}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderOwnerInput = () => (
    <div className="d-flex form-group position-relative kdf-filter-owner">
      <input
        className="kdf-Tags2"
        type="text"
        placeholder="Owner"
        value={ownerName}
        onChange={handleOwnerNameChange}
        onKeyDown={handleKeyDown}
      />
      {ownerSearchList.length > 0 && (
        <ul className="kdf-title-drop-ul">
          {ownerSearchList.map((item, index) => (
            <li
              key={index}
              className="cursor-pointer kdf-title-drop-li"
              onClick={() => handleOwnerSelect(item)}
            >
              {item.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderTagsInput = () => (
    <div className="d-flex form-group kdf-list-inp">
      <CustomTagsInput
        tags={kdfTags?.tags || []}
        handleRemoveTags={(value) =>
          setKdfTags((prev) => ({ ...prev, tags: value }))
        }
        placeholderText={'KDF tags'}
        autoSubmit={true}
        collapsedWhenHasTags={false}
        triggerKeyDownEvent={(e) => {
          handleKdfTagsInputChange(e);
          handleKeyDown(e);
        }}
      />
      {tagsSearchList.length > 0 && (
        <ul className="kdf-title-drop-ul">
          {tagsSearchList.map((item, index) => (
            <li
              key={index}
              className="cursor-pointer kdf-title-drop-li"
              onClick={() => handleTagSelect(item)}
            >
              {item.name}
            </li>
          ))}
        </ul>
      )}
      {noMatchFound && (
        <ul className="kdf-title-drop-ul">
          <li className="no-match kdf-title-drop-li">No match found</li>
        </ul>
      )}
    </div>
  );

  const renderActionButtons = () => (
    <div className="kdf-filter-actions">
      <button
        type="button"
        className="btn btn-info margin-left-15 reset"
        onClick={applyFilters}
        disabled={loading}
      >
        {loading ? "Applying Filters" : "Apply Filters"}
      </button>
      <button className="reset-btn" type="button" onClick={resetFilters}>
        <img src="/icons/autorenew.svg" /> Reset Filters
      </button>
    </div>
  );

  return (
    <>
      <form className="kdf-main-list filterForm">
        <div className="kdf-filters-row">
          {isOpen && renderStatusDropdown()}
          {isOpen && renderSortDropdown()}
          {isOpen && renderOwnerInput()}
          {isOpen && renderTagsInput()}
          {isOpen && renderSearchInput()}
          {isOpen && renderActionButtons()}
        </div>
      </form>
      {!isOpen && quickBarTarget && ReactDOM.createPortal(
        <>
          {renderSearchInput()}
          {renderActionButtons()}
        </>,
        quickBarTarget
      )}
    </>
  );
};

export default KdfFilerForm;
